/* Cosmeau V6: Juo + Corner Cart subscription checkout helper (live parity, hardened). */
(function () {
  class CartHttpError {
    constructor(status, description) {
      this.status = status;
      this.message = 'Cart Error';
      this.description = description || 'Cart Error';
    }
  }

  class CartWatcher {
    init() {
      if (this.currentCart) return Promise.resolve(this.currentCart);
      return this.emitCartChanges().then((cart) => {
        this.observeCartChanges();
        return cart;
      });
    }

    checkResponse(data) {
      if (data && data.status === 404) throw new CartHttpError(404, 'Cannot find variant');
      if (data && data.status === 422) throw new CartHttpError(422, data.description);
      if (data && data.token) return data;
      throw new Error('No token provided in the Shopify Cart response');
    }

    async fetchCart() {
      const res = await fetch('/cart.js?juo_watch=1', {
        headers: { cache: 'no-store' },
        credentials: 'same-origin',
      });
      const data = await res.json();
      return this.checkResponse(data);
    }

    storeCart(cart) {
      try {
        localStorage.setItem('juo-cart', JSON.stringify(cart));
      } catch (_) {}
    }

    storedCart() {
      try {
        const raw = localStorage.getItem('juo-cart');
        return raw ? JSON.parse(raw) : { items: [] };
      } catch (_) {
        return { items: [] };
      }
    }

    findCartChanges(prev, next) {
      const prevItems = (prev && prev.items) || [];
      const nextItems = (next && next.items) || [];
      const missing = (a, b) => a.filter((item) => !b.some((x) => x.key === item.key));
      const changes = {
        added: missing(nextItems, prevItems),
        removed: missing(prevItems, nextItems),
      };

      prevItems.forEach((oldItem) => {
        const newer = nextItems.find((x) => x.key === oldItem.key && x.quantity !== oldItem.quantity);
        if (!newer) return;
        const delta = newer.quantity - oldItem.quantity;
        const copy = Object.assign({}, newer, { quantity: Math.abs(delta) });
        if (delta > 0) changes.added.push(copy);
        else changes.removed.push(copy);
      });

      return changes;
    }

    async emitCartChanges(providedCart) {
      const cart = providedCart || await this.fetchCart();
      const prev = this.storedCart();
      const changes = this.findCartChanges(prev, cart);
      this.storeCart(cart);
      this.currentCart = cart;
      if (changes.added.length || changes.removed.length) {
        window.dispatchEvent(new CustomEvent('cart_changed', { detail: changes }));
      }
      return cart;
    }

    scheduleEmit() {
      clearTimeout(this.emitTimer);
      this.emitTimer = setTimeout(() => this.emitCartChanges().catch(() => {}), 120);
    }

    observeCartChanges() {
      if (this.cartObserver) return;
      try {
        this.cartObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            const isNetwork = ['xmlhttprequest', 'fetch'].includes(entry.initiatorType);
            if (isNetwork && /\/cart\//.test(entry.name) && !/juo_watch=1/.test(entry.name)) this.scheduleEmit();
          });
        });
        this.cartObserver.observe({ entryTypes: ['resource'] });
      } catch (_) {
        this.cartObserver = null;
      }
    }

    stop() {
      try {
        if (this.cartObserver) this.cartObserver.disconnect();
      } catch (_) {}
      this.cartObserver = null;
      clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }
  }

  const HAS_SUB_CLASS = 'has-subscription';
  const IDEAL_SELECTOR = '#juo-go-to-checkout-with-ideal';
  const DEFAULT_TPL_ID = 'juo-cart-default-button';
  const CTA_SELECTOR = '#corner-cowi-cart-summary-card-cta-button, #CartDrawer-Checkout';
  const WRAPPER_SELECTOR = '#corner-cowi-cart-cta-wrapper, #CosmeauCartCheckout';

  let uiReady = false;
  let originalCtaHtml = '';
  let ensureUiPromise = null;
  const fixedPlanByKey = new Map();
  const watcher = new CartWatcher();

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function checkoutUrl(skipShopPay) {
    const base = rootUrl().replace(/\/?$/, '/');
    return skipShopPay ? base + 'checkout?skip_shop_pay=true' : base + 'checkout';
  }

  function goCheckout(skipShopPay) {
    // Keep Cosmeau's current Juo/Mollie iDEAL checkout available in both
    // Corner and the native drawer until the replacement subscription stack ships.
    if (skipShopPay && typeof window.fhSepaCheckout === 'function') {
      window.fhSepaCheckout();
      return;
    }
    window.location.href = checkoutUrl(skipShopPay);
  }

  function withTimeout(promise, timeoutMs) {
    let timer;
    const timeout = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        reject(new Error('Checkout request timeout'));
      }, timeoutMs);
    });
    return Promise.race([Promise.resolve(promise), timeout]).finally(function () {
      clearTimeout(timer);
    });
  }

  function isSubscriptionItem(item) {
    return !!(item && item.selling_plan_allocation);
  }

  function isFixedPlan(item) {
    const plan = item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    if (!plan) return false;
    if (plan.fixed_selling_plan === true) return true;
    if (plan.recurring_deliveries === false) return true;
    return false;
  }

  function parentRecurringId(item) {
    const raw = item && item.properties && item.properties._selling_plan_id;
    if (raw) return String(raw);
    const plan = item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    const opt = plan && Array.isArray(plan.options) ? plan.options[0] : null;
    const value = opt && opt.value ? String(opt.value) : '';
    const match = value.match(/parent:(\d+)/);
    return match ? match[1] : '';
  }

  function parseSchedule(item) {
    const plan = item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    const opt = plan && Array.isArray(plan.options) ? plan.options[0] : null;
    const value = opt && opt.value ? String(opt.value) : '';
    if (!value) return null;
    const df = value.match(/df:(\d+)\s*([A-Z]+)/i);
    const bf = value.match(/bf:(\d+)\s*([A-Z]+)/i);
    if (!df || !bf) return null;
    const delivery = Number.parseInt(df[1], 10);
    const billing = Number.parseInt(bf[1], 10);
    return {
      delivery: delivery,
      billing: billing,
      same: delivery === billing,
    };
  }

  function markSubscriptionWrapper() {
    const wrap = document.querySelector(WRAPPER_SELECTOR);
    if (wrap && !wrap.classList.contains(HAS_SUB_CLASS)) wrap.classList.add(HAS_SUB_CLASS);
  }

  function bindDefaultCta(cta) {
    if (!cta || cta.dataset.juoCheckoutBound === '1') return;
    cta.dataset.juoCheckoutBound = '1';
  }

  function onDefaultClick(event, cta) {
    event.preventDefault();
    event.stopPropagation();
    if (cta.dataset.juoCheckoutPending === '1') return;
    cta.dataset.juoCheckoutPending = '1';
    cta.setAttribute('aria-busy', 'true');
    try {
      goCheckout(false);
    } finally {
      delete cta.dataset.juoCheckoutPending;
      cta.removeAttribute('aria-busy');
    }
  }

  function onDelegatedCheckoutClick(event) {
    const target = event.target && event.target.closest ? event.target : null;
    const ideal = target?.closest(IDEAL_SELECTOR);
    if (ideal) {
      onIdealClick(event).catch(function (error) {
        console.error('[juo-checkout] ideal click failed open', error);
        ideal.removeAttribute('disabled');
        goCheckout(true);
      });
      return;
    }
    const cta = target?.closest(CTA_SELECTOR);
    if (cta) onDefaultClick(event, cta);
  }

  function maybeEnableIdeal() {
    const ideal = document.querySelector(IDEAL_SELECTOR);
    if (!ideal) return;

    const cart = watcher.storedCart();
    const subs = ((cart && cart.items) || []).filter(isSubscriptionItem);
    if (!subs.length) {
      ideal.removeAttribute('disabled');
      return;
    }

    const ready = subs.every(function (item) {
      const key = String(item.key);
      if (fixedPlanByKey.has(key) && fixedPlanByKey.get(key) != null) return true;
      // Already prepaid/fixed in cart: iDEAL can proceed without remap.
      return isFixedPlan(item);
    });

    ideal.dataset.juoPlanReady = ready ? '1' : '0';
    ideal.removeAttribute('disabled');
  }

  async function loadFixedPlanMap() {
    fixedPlanByKey.clear();

    try {
      const res = await fetch('/?section_id=juo-selling-plans', { credentials: 'same-origin' });
      if (res.ok) {
        const html = await res.text();
        const box = document.createElement('div');
        box.innerHTML = html;
        const tpl = box.querySelector('#juo-selling-plans-map, template');
        const parsed = JSON.parse((tpl && tpl.innerHTML) || '{}');
        Object.keys(parsed || {}).forEach(function (key) {
          const value = parsed[key];
          if (value != null && value !== '') fixedPlanByKey.set(String(key), value);
        });
      } else {
        console.error('[juo-checkout] Failed to load selling plans section', res.status);
      }
    } catch (err) {
      console.error('[juo-checkout] Failed to parse selling plans map', err);
    }

    // Fallback for null Liquid maps: use fixed plan already present on cart lines.
    try {
      let cart = watcher.storedCart();
      if (!cart || !cart.items || !cart.items.length) cart = await watcher.fetchCart();
      (cart.items || []).forEach(function (item) {
        if (!isSubscriptionItem(item)) return;
        const key = String(item.key);
        if (fixedPlanByKey.has(key)) return;
        if (isFixedPlan(item) && item.selling_plan_allocation.selling_plan.id) {
          fixedPlanByKey.set(key, item.selling_plan_allocation.selling_plan.id);
        }
      });
    } catch (_) {}

    maybeEnableIdeal();
  }

  async function onIdealClick(event) {
    const btn =
      event && event.target && event.target.closest
        ? event.target.closest(IDEAL_SELECTOR)
        : document.querySelector(IDEAL_SELECTOR);
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();
    btn.setAttribute('disabled', 'disabled');

    let cart = watcher.storedCart();
    if (!cart?.items?.length) {
      try {
        cart = await withTimeout(watcher.fetchCart(), 900);
        watcher.storeCart(cart);
      } catch (_) {}
    }

    let subItems = (cart.items || []).filter(isSubscriptionItem);
    if (!subItems.length) {
      btn.removeAttribute('disabled');
      goCheckout(false);
      return;
    }

    // Current cart is often already converted to a prepaid/fixed plan after reopen.
    if (subItems.every(isFixedPlan)) {
      btn.removeAttribute('disabled');
      goCheckout(true);
      return;
    }

    try {
      await withTimeout(loadFixedPlanMap(), 1200);
    } catch (error) {
      console.warn('[juo-checkout] selling plan map timeout, checkout gaat door', error);
    }

    cart = watcher.storedCart();
    subItems = (cart.items || []).filter(isSubscriptionItem);

    const conversions = [];
    subItems.forEach(function (item) {
      const key = String(item.key);
      let fixedId = fixedPlanByKey.get(key);
      if (fixedId == null && isFixedPlan(item)) {
        fixedId = item.selling_plan_allocation.selling_plan.id;
      }
      if (fixedId == null) return;
      conversions.push({ item: item, fixedId: fixedId });
    });

    // Never leave the shopper with a dead button.
    if (!conversions.length) {
      btn.removeAttribute('disabled');
      goCheckout(true);
      return;
    }

    try {
      for (let i = 0; i < conversions.length; i++) {
        const item = conversions[i].item;
        const fixedId = conversions[i].fixedId;
        const recurringId =
          parentRecurringId(item) ||
          (item.selling_plan_allocation &&
            item.selling_plan_allocation.selling_plan &&
            item.selling_plan_allocation.selling_plan.id) ||
          '';

        const body = {
          id: item.key,
          quantity: item.quantity,
          properties: Object.assign({}, item.properties || {}, {
            _selling_plan_id: String(recurringId || ''),
          }),
          selling_plan: fixedId,
        };

        const res = await withTimeout(fetch(rootUrl().replace(/\/?$/, '/') + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        }), 1800);
        if (!res.ok) throw new Error('Failed to convert subscription line for iDEAL checkout');
      }
      btn.removeAttribute('disabled');
      goCheckout(true);
    } catch (err) {
      console.error('[juo-checkout] ideal convert failed', err);
      btn.removeAttribute('disabled');
      goCheckout(true);
    }
  }

  async function ensureUi(providedCart) {
    if (providedCart && providedCart.items) {
      watcher.storeCart(providedCart);
      watcher.currentCart = providedCart;
    }
    if (ensureUiPromise) return ensureUiPromise;
    ensureUiPromise = (async function () {
      const cart = providedCart && providedCart.items ? providedCart : await watcher.init();
      const hasSub = (cart.items || []).some(isSubscriptionItem);
      const cta = document.querySelector(CTA_SELECTOR);
      const wrap = cta ? cta.closest(WRAPPER_SELECTOR) : document.querySelector(WRAPPER_SELECTOR);

      if (!hasSub) {
        const idealWrap = document.querySelector('.juo-ideal-checkout-link-wrapper');
        if (idealWrap) idealWrap.remove();
        if (cta && originalCtaHtml) {
          cta.innerHTML = originalCtaHtml;
          cta.classList.remove('juo-adjustments');
          delete cta.dataset.juoCheckoutBound;
        }
        if (wrap) wrap.classList.remove(HAS_SUB_CLASS);
        return;
      }

      const badCadence = (cart.items || []).some(function (item) {
        if (!isSubscriptionItem(item)) return false;
        const schedule = parseSchedule(item);
        return schedule ? !schedule.same : false;
      });
      if (badCadence) {
        const existing = document.querySelector(IDEAL_SELECTOR);
        if (existing && existing.closest) {
          const holder = existing.closest('.juo-ideal-checkout-link-wrapper');
          if (holder) holder.remove();
          else existing.remove();
        }
        if (cta) bindDefaultCta(cta);
        return;
      }

      if (!cta || !wrap) return;

      markSubscriptionWrapper();

      if (!originalCtaHtml) originalCtaHtml = cta.innerHTML;

      const existingIdeal = document.querySelector(IDEAL_SELECTOR);
      if (!existingIdeal) {
        const idealTpl = document.getElementById('juo-ideal-checkout-link');
        const defaultTpl = document.getElementById(DEFAULT_TPL_ID);
        if (!idealTpl) return;

        if (defaultTpl) {
          cta.innerHTML = defaultTpl.innerHTML;
          cta.classList.add('juo-adjustments');
          delete cta.dataset.juoCheckoutBound;
        }

        const holder = document.createElement('div');
        holder.className = 'juo-ideal-checkout-link-wrapper';
        holder.innerHTML = idealTpl.innerHTML;
        wrap.insertBefore(holder, cta);
        wrap.classList.add(HAS_SUB_CLASS);

        const idealBtn = holder.querySelector('button');
        if (idealBtn) idealBtn.removeAttribute('disabled');
        uiReady = true;
      }

      bindDefaultCta(cta);
      await loadFixedPlanMap();
    })();

    try {
      await ensureUiPromise;
    } finally {
      ensureUiPromise = null;
    }
  }

  async function restoreRecurringAfterCheckoutPrep() {
    let cart;
    try {
      cart = await watcher.fetchCart();
    } catch (_) {
      return;
    }

    const locked = (cart.items || []).filter(function (item) {
      return item.properties && item.properties._selling_plan_id && String(item.properties._selling_plan_id).length;
    });
    if (!locked.length) return;

    watcher.stop();
    try {
      for (let i = 0; i < locked.length; i++) {
        const item = locked[i];
        const recurringId = String(item.properties._selling_plan_id);
        const currentId =
          item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
            ? String(item.selling_plan_allocation.selling_plan.id)
            : '';
        if (currentId && currentId === recurringId) continue;

        const res = await fetch(rootUrl().replace(/\/?$/, '/') + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: Object.assign({}, item.properties || {}, { _selling_plan_id: '' }),
            selling_plan: recurringId,
          }),
        });
        if (!res.ok) throw new Error('Failed to restore recurring selling plan');
      }
    } catch (err) {
      console.error('[juo-checkout] restore recurring failed', err);
    } finally {
      watcher.observeCartChanges();
      try {
        await watcher.emitCartChanges();
      } catch (_) {}
    }
  }

  async function boot() {
    if (!window.Shopify) return;

    if (!window.__cosmeauDelegatedCheckoutBound) {
      window.__cosmeauDelegatedCheckoutBound = true;
      document.addEventListener('click', onDelegatedCheckoutClick, true);
    }

    const firstCta = document.querySelector(CTA_SELECTOR);
    if (firstCta) originalCtaHtml = firstCta.innerHTML;

    let observerTimer = null;
    const observer = new MutationObserver(function () {
      if (!document.querySelector(WRAPPER_SELECTOR) && !document.querySelector(CTA_SELECTOR)) return;
      clearTimeout(observerTimer);
      observerTimer = setTimeout(function () {
        ensureUi(watcher.currentCart).catch(function (err) {
          console.error('[juo-checkout] ensureUi failed', err);
        });
      }, 80);
    });
    try {
      observer.observe(document.body, { childList: true, subtree: true });
    } catch (_) {}

    window.addEventListener('cart_changed', function (event) {
      ensureUi(event.detail && event.detail.cart).catch(function (err) {
        console.error('[juo-checkout] cart_changed failed', err);
      });
    });

    try {
      if (window.corner && typeof window.corner.on === 'function') {
        window.corner.on('onCartEdit', function (payload) {
          const items = (payload && payload.cartDetails && payload.cartDetails.items) || [];
          if (items.some(isSubscriptionItem)) markSubscriptionWrapper();
        });
      }
    } catch (_) {}

    setTimeout(function () {
      restoreRecurringAfterCheckoutPrep()
        .catch(function () {})
        .finally(function () {
          ensureUi().catch(function () {});
        });
    }, 400);

    [0, 700].forEach(function (ms) {
      setTimeout(function () {
        ensureUi().catch(function () {});
      }, ms);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }
})();
